import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "./context/LanguageContext";
import { useForm } from "./context/FormContext";
import Header from "./components/Header";
import ProgressBar from "./components/ProgressBar";
import HelpOverlay from "./components/HelpOverlay";
import FormNavigation from "./components/FormNavigation";
import LandingScreen from "./components/LandingScreen";
import FloatingHelpButton from "./components/FloatingHelpButton";
import CompletionScreen from "./components/CompletionScreen";
import ConfirmDetailsSection from "./components/sections/ConfirmDetailsSection";
import EngagementSection from "./components/sections/EngagementSection";
import AddressSection from "./components/sections/AddressSection";
import EligibilityRoutingSection from "./components/sections/EligibilityRoutingSection";
import DocRequirementsSection from "./components/sections/DocRequirementsSection";
import DocumentFlow from "./components/DocumentFlow";
import OrganizerView from "./components/OrganizerView";
import { mvpApi } from "../services/api";
import {
  clearApplicantAuthSession,
  isApplicantSessionIdleExpired,
  markApplicantSessionStarted,
  touchApplicantActivity,
} from "../utils/applicantSession";

import Footer from "./components/Footer";
import ExitModal from "./components/ExitModal";
import {
  isMonthYearComplete,
} from "./utils/datePartsValidation";
import {
  isValidEmail,
  isValidNameField,
  isValidOptionalPhoneParts,
  isValidRequiredPhoneParts,
} from "./utils/contactFieldValidation";
import { getAddressStep5FirstInvalidFieldId } from "./utils/addressFieldValidation";
import { isEngagementSignatureDataUrlValid } from "./utils/signatureFieldValidation";

const APPLICANT_ENTRY_INTENT_KEY = "mvp_applicant_entry_intent";
const LANDING_DISMISSED_KEY = "mvp_applicant_landing_dismissed_v1";
const DOCS_OPEN_KEY = "mvp_applicant_docs_open_v1";
const DEFAULT_LOCALE = "es";
const localizedPath = (path: string): string => `/${DEFAULT_LOCALE}${path}`;

function readLandingDismissedFlag(): boolean {
  try {
    return sessionStorage.getItem(LANDING_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function readDocumentsOpenFlag(): boolean {
  try {
    return sessionStorage.getItem(DOCS_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

type PendingOrganizerDocs = { category: string | null };

export function ApplicantPortalContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathHub = location.pathname === localizedPath("/organizer") ? "organizer" : "intake";
  /** Avoid opening doc overlay while still on /intake (Safari defers navigation); see intake `documentsOpen` reset effect. */
  const pendingOrganizerDocsRef = useRef<PendingOrganizerDocs | null>(null);

  const [helpOpen, setHelpOpen] = useState(false);
  const [helpField, setHelpField] = useState("");
  const [landingDismissed, setLandingDismissedState] = useState(readLandingDismissedFlag);
  const [showExitModal, setShowExitModal] = useState(false);
  const [documentsOpen, setDocumentsOpenState] = useState(readDocumentsOpenFlag);
  const [previousHub, setPreviousHub] = useState<"organizer" | null>(null);
  const [initialDocCategory, setInitialDocCategory] = useState<string | null>(null);
  const [stepSaving, setStepSaving] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [invalidFieldId, setInvalidFieldId] = useState<string | null>(null);
  const [eligibilitySubstep, setEligibilitySubstep] = useState<"asylum" | "qualifying">("asylum");
  const { currentStep, totalSteps, helpRequest, clearHelpRequest, data, goToStep, goBack } = useForm();
  const { isRTL, t } = useLang();
  const mainRef = useRef<HTMLElement>(null);

  const persistLandingDismissed = useCallback((value: boolean) => {
    setLandingDismissedState(value);
    try {
      if (value) sessionStorage.setItem(LANDING_DISMISSED_KEY, "1");
      else sessionStorage.removeItem(LANDING_DISMISSED_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const dismissLanding = useCallback(() => persistLandingDismissed(true), [persistLandingDismissed]);

  const setDocumentsOpen = useCallback((open: boolean) => {
    setDocumentsOpenState(open);
    try {
      if (open) sessionStorage.setItem(DOCS_OPEN_KEY, "1");
      else sessionStorage.removeItem(DOCS_OPEN_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const goToOrganizer = useCallback(() => {
    setDocumentsOpen(false);
    navigate(localizedPath("/organizer"));
  }, [navigate, setDocumentsOpen]);

  const goToIntake = useCallback(() => {
    setDocumentsOpen(false);
    navigate(localizedPath("/intake"));
  }, [navigate, setDocumentsOpen]);

  const applyPendingOrganizerDocuments = useCallback(() => {
    const pending = pendingOrganizerDocsRef.current;
    if (!pending) return;
    pendingOrganizerDocsRef.current = null;
    setInitialDocCategory(pending.category);
    setDocumentsOpen(true);
  }, [setDocumentsOpen]);

  const openDocuments = useCallback(
    (category?: string | null) => {
      pendingOrganizerDocsRef.current = { category: category ?? null };
      if (pathHub === "organizer") {
        applyPendingOrganizerDocuments();
        return;
      }
      navigate(localizedPath("/organizer"));
    },
    [navigate, pathHub, applyPendingOrganizerDocuments]
  );

  useEffect(() => {
    if (pathHub !== "organizer") return;
    applyPendingOrganizerDocuments();
  }, [pathHub, applyPendingOrganizerDocuments]);

  const isCompletion = currentStep > totalSteps;
  const shouldBlock =
    landingDismissed &&
    !isCompletion &&
    pathHub === "intake" &&
    !documentsOpen &&
    previousHub !== "organizer";

  useEffect(() => {
    try {
      if (sessionStorage.getItem(APPLICANT_ENTRY_INTENT_KEY) === "login") {
        sessionStorage.removeItem(APPLICANT_ENTRY_INTENT_KEY);
        persistLandingDismissed(true);
        navigate(localizedPath("/organizer"), { replace: true });
      }
    } catch {
      // ignore storage read errors
    }
  }, [navigate, persistLandingDismissed]);

  useEffect(() => {
    if (pathHub === "intake" && documentsOpen) setDocumentsOpen(false);
  }, [pathHub, documentsOpen, setDocumentsOpen]);

  useEffect(() => {
    markApplicantSessionStarted();

    let lastTouch = 0;
    const touch = () => {
      const now = Date.now();
      if (now - lastTouch < 15000) return;
      lastTouch = now;
      touchApplicantActivity(now);
    };
    const enforceExpiry = () => {
      if (!isApplicantSessionIdleExpired()) return;
      clearApplicantAuthSession();
      window.location.assign("/login");
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
      "focus",
    ];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, touch, { passive: true });
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        touch();
        enforceExpiry();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    const timer = window.setInterval(enforceExpiry, 60000);

    return () => {
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, touch);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(timer);
    };
  }, []);

  // Push a sentinel history entry + intercept popstate (browser/Android back button)
  useEffect(() => {
    if (!shouldBlock) return;

    // Push an extra history entry so the first "back" hits our handler instead of leaving
    window.history.pushState({ exitGuard: true }, "", window.location.href);

    const handlePopState = () => {
      const st = window.history.state as { __gmScanner?: boolean } | null;
      // Document scanner pushes __gmScanner layers; its pop handler manages sheet/camera back - skip intake exit modal here.
      if (st?.__gmScanner) return;
      // Re-push so repeated back presses keep hitting this handler
      window.history.pushState({ exitGuard: true }, "", window.location.href);
      setShowExitModal(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [shouldBlock]);

  // Catch tab/window close
  useEffect(() => {
    if (!shouldBlock) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldBlock]);

  // Scroll to top on every step change - covers both the main container and the window
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentStep, pathHub, documentsOpen]);

  useEffect(() => {
    if (helpRequest) {
      setHelpField(helpRequest);
      setHelpOpen(true);
      clearHelpRequest();
    }
  }, [helpRequest, clearHelpRequest]);

  useEffect(() => {
    if (currentStep !== 2) {
      setEligibilitySubstep("asylum");
      return;
    }
    setEligibilitySubstep("asylum");
  }, [currentStep]);

  const handleHelp = useCallback(() => {
    setHelpField("");
    setHelpOpen(true);
  }, []);

  const handleCloseHelp = useCallback(() => {
    setHelpOpen(false);
    setHelpField("");
  }, []);

  const handleExitStay = useCallback(() => {
    setShowExitModal(false);
  }, []);

  const handleExitLeave = useCallback(() => {
    setShowExitModal(false);
    // Go back two entries: past our sentinel + one more to actually leave
    window.history.go(-2);
  }, []);

  const toE164 = (phoneCountryCode: string, phoneRaw: string): string => {
    const raw = (phoneRaw || "").trim();
    if (!raw) return "";
    const compact = raw.replace(/[^\d+]/g, "");
    if (compact.startsWith("+")) return compact;
    const cc = (phoneCountryCode || "").replace(/[^\d+]/g, "") || "+34";
    return `${cc}${compact}`.replace(/\s+/g, "");
  };

  const handleStepContinue = useCallback(async () => {
    if (currentStep !== 1 && currentStep !== 2 && currentStep !== 3 && currentStep !== 4) {
      setStepError(null);
      return true;
    }
    try {
      setStepError(null);
      setStepSaving(true);
      if (currentStep === 1) {
        const firstName = data.firstName.trim();
        const firstSurname = data.primerApellido.trim();
        const secondSurname = data.segundoApellido.trim();
        const lastName = firstSurname;
        const applicantType = data.registrantType === "representative" ? "others" : "self";
        const payload: Record<string, unknown> = {
          first_name: firstName,
          last_name: lastName,
          second_surname: secondSurname || null,
          middle_name: null,
          email: data.email.trim().toLowerCase() || null,
          phone: toE164(data.phoneCountryCode, data.phone),
          applicant_type: applicantType,
          civil_status: data.civilStatus || null,
          father_first_name: data.fatherUnknown === "yes" ? null : data.fatherFirstName.trim() || null,
          mother_first_name: data.motherUnknown === "yes" ? null : data.motherFirstName.trim() || null,
          father_not_known: data.fatherUnknown === "yes",
          mother_not_known: data.motherUnknown === "yes",
        };
        if (applicantType === "others") {
          payload.rep_relationship_to_applicant = data.repRelationship.trim() || null;
          payload.applicant_first_name = data.applicantFirstName.trim() || null;
          payload.applicant_primer_apellido = data.applicantPrimerApellido.trim() || null;
          payload.applicant_segundo_apellido = data.applicantSegundoApellido.trim() || null;
          payload.applicant_email = data.applicantEmail.trim().toLowerCase() || null;
          const appPhoneRaw = data.applicantPhone.trim();
          payload.applicant_phone = appPhoneRaw
            ? toE164(data.applicantPhoneCountryCode, data.applicantPhone)
            : null;
        }
        await mvpApi.updatePersonalDetails(payload);
      } else if (currentStep === 2) {
        if (eligibilitySubstep === "asylum") {
          // Split flow:
          // - "Yes" can be saved immediately.
          // - "No" must first advance to qualifying page; submit there with qualifying_ground.
          if (data.asylumRoutingConfirmed === true) {
            await mvpApi.updateEligibilityRouting({
              asylum_applicant: true,
              qualifying_ground: null,
            });
          }
        } else {
          const ground =
            data.qualifyingGround === "work" ||
            data.qualifyingGround === "family" ||
            data.qualifyingGround === "vulnerability"
              ? data.qualifyingGround
              : null;
          await mvpApi.updateEligibilityRouting({
            asylum_applicant: false,
            qualifying_ground: ground,
          });
        }
      } else if (currentStep === 3) {
        const rs = data.residenceSince;
        const residenceSinceIso =
          isMonthYearComplete(rs.month ?? "", rs.year ?? "")
            ? `${String(rs.year ?? "").trim().padStart(4, "0")}-${String(rs.month ?? "").trim().padStart(2, "0")}-01`
            : null;
        await mvpApi.updateAddressDetails({
          street_name: data.street.trim(),
          street_type: data.streetType.trim() || null,
          street_number: data.streetNumber.trim(),
          floor_piso: data.floorPiso.trim(),
          floor_door: data.floorDoor.trim() || null,
          municipality: data.municipality.trim() || null,
          locality: data.locality.trim() || null,
          city_locality: (data.locality.trim() || data.city.trim()),
          province: data.province.trim(),
          postal_code: data.postalCode.trim(),
          country: data.country.trim(),
          residence_since: residenceSinceIso ?? null,
        });
      } else if (currentStep === 4) {
        await mvpApi.updateEngagementDetails({
          no_return_commitment: data.noReturnCommitment === true,
          no_healthcare_coverage: data.noHealthcareCoverage === true,
          engagement_consent: data.engagementConsent === true,
          data_processing_consent: data.dataProcessingConsent === true,
          marketing_consent: false,
          questions_requested: data.questionsRequested === true,
          signature_data_url: data.signature,
        });
      }
      return true;
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "No pudimos guardar tus datos. Inténtalo de nuevo.";
      setStepError(typeof detail === "string" ? detail : "No pudimos guardar tus datos. Inténtalo de nuevo.");
      return false;
    } finally {
      setStepSaving(false);
    }
  }, [currentStep, data, eligibilitySubstep]);

  useEffect(() => {
    if (!invalidFieldId) return;
    const stillInvalid = (() => {
      switch (currentStep) {
        case 1:
          if (!(data.registrantType === "individual" || data.registrantType === "representative")) {
            return invalidFieldId === "field-registrantType";
          }
          if (!data.firstName.trim() || !isValidNameField(data.firstName, true)) {
            return invalidFieldId === "field-firstName";
          }
          if (!data.primerApellido.trim() || !isValidNameField(data.primerApellido, true)) {
            return invalidFieldId === "field-primerApellido";
          }
          if (!isValidNameField(data.segundoApellido, false)) return invalidFieldId === "field-segundoApellido";
          if (data.fatherUnknown !== "yes" && !data.fatherFirstName.trim()) {
            return invalidFieldId === "field-fatherFirstName";
          }
          if (data.motherUnknown !== "yes" && !data.motherFirstName.trim()) {
            return invalidFieldId === "field-motherFirstName";
          }
          if (!data.civilStatus) return invalidFieldId === "field-civilStatus";
          if (data.email.trim() && !isValidEmail(data.email)) return invalidFieldId === "field-email";
          if (!data.phone.trim() || !isValidRequiredPhoneParts(data.phoneCountryCode, data.phone)) {
            return invalidFieldId === "field-phone";
          }
          if (data.registrantType === "representative") {
            if (!data.repRelationship.trim()) return invalidFieldId === "field-repRelationship";
            if (!data.applicantFirstName.trim() || !isValidNameField(data.applicantFirstName, true)) {
              return invalidFieldId === "field-applicantFirstName";
            }
            if (!data.applicantPrimerApellido.trim() || !isValidNameField(data.applicantPrimerApellido, true)) {
              return invalidFieldId === "field-applicantPrimerApellido";
            }
            if (!isValidNameField(data.applicantSegundoApellido, false)) {
              return invalidFieldId === "field-applicantSegundoApellido";
            }
            const appEmail = data.applicantEmail.trim();
            if (appEmail && !isValidEmail(appEmail)) return invalidFieldId === "field-applicantEmail";
            if (
              data.applicantPhone.trim() &&
              !isValidOptionalPhoneParts(data.applicantPhoneCountryCode, data.applicantPhone)
            ) {
              return invalidFieldId === "field-applicantPhone";
            }
            if (data.contactAssignedTo === "applicant" && !data.repPhoneOtpVerified) {
              return invalidFieldId === "field-repPhoneOtpVerified";
            }
          }
          return false;
        case 2: {
          if (eligibilitySubstep === "asylum") {
            if (data.asylumRoutingConfirmed === null) return invalidFieldId === "field-asylumRoutingConfirmed";
            return false;
          }
          if (!data.qualifyingGround) {
            return invalidFieldId === "field-qualifyingGround";
          }
          return false;
        }
        case 3: {
          const first = getAddressStep5FirstInvalidFieldId(data);
          return first === invalidFieldId;
        }
        case 4:
          if (!data.engagementConsent) return invalidFieldId === "field-engagementConsent";
          if (!data.dataProcessingConsent) return invalidFieldId === "field-dataProcessingConsent";
          if (!data.signature || !isEngagementSignatureDataUrlValid(data.signature)) {
            return invalidFieldId === "field-signature";
          }
          return false;
        default:
          return false;
      }
    })();
    if (stillInvalid) return;
    const root = mainRef.current || document;
    const target =
      root.querySelector<HTMLElement>(`#${invalidFieldId}`) || document.getElementById(invalidFieldId);
    if (target) target.classList.remove("app-field-attention");
    setInvalidFieldId(null);
  }, [invalidFieldId, currentStep, data, eligibilitySubstep]);

  // View routing - documents overlay on /organizer, then organizer hub, then /intake (MUST be before landing screen check)
  if (pathHub === "organizer" && documentsOpen) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col" dir={isRTL ? "rtl" : "ltr"}>
        <Header onHelp={() => {}} hideHelp onLogoClick={goToOrganizer} />
        <DocumentFlow
          onBack={() => setDocumentsOpen(false)}
          initialCategory={initialDocCategory}
        />
      </div>
    );
  }
  if (pathHub === "organizer" && !documentsOpen) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col" dir={isRTL ? "rtl" : "ltr"}>
        <Header onHelp={() => {}} hideHelp onLogoClick={goToOrganizer} />
        <OrganizerView
          onUploadDocs={() => {
            setInitialDocCategory(null);
            setDocumentsOpen(true);
          }}
          onGoToStep={(step) => {
            setPreviousHub("organizer");
            persistLandingDismissed(true);
            goToStep(step);
            goToIntake();
          }}
          onGoToDocCategory={(cat) => {
            setInitialDocCategory(cat);
            setDocumentsOpen(true);
          }}
        />
      </div>
    );
  }

  if (pathHub === "intake" && !landingDismissed) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col" dir={isRTL ? "rtl" : "ltr"}>
        <Header onHelp={handleHelp} hideHelp onLogoClick={goToOrganizer} />
        <LandingScreen
          onGetStarted={dismissLanding}
        />
        {helpOpen && <HelpOverlay onClose={handleCloseHelp} fieldTag={helpField} />}
      </div>
    );
  }

  if (pathHub === "intake" && isCompletion) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col" dir={isRTL ? "rtl" : "ltr"}>
        <Header onHelp={() => {}} hideHelp onLogoClick={goToOrganizer} />
        <CompletionScreen
          onNavigate={(target, category) => {
            if (target === "documents") {
              setInitialDocCategory(category ?? null);
              openDocuments();
            } else {
              goToOrganizer();
            }
          }}
        />
      </div>
    );
  }

  const renderSection = () => {
    switch (currentStep) {
      case 1: return <ConfirmDetailsSection />;
      case 2: return <EligibilityRoutingSection mode={eligibilitySubstep} />;
      case 3: return <AddressSection />;
      case 4: return <EngagementSection />;
      default: return <EngagementSection />;
    }
  };

  // Validation logic: required fields must be complete before continuing
  const canContinue = (() => {
    switch (currentStep) {
      case 1: {
        // Step 1: names, parent names / unknown flags, civil status, and account contact.
        // Representative ("others"): also relationship + applicant first name + first surname (required);
        // applicant second surname, email, phone stay optional.
        const {
          firstName,
          primerApellido,
          email,
          phone,
          repRelationship,
          applicantFirstName,
          applicantPrimerApellido,
          contactAssignedTo,
          repPhoneOtpVerified,
        } = data;
        if (!firstName.trim() || !primerApellido.trim() || !phone.trim()) return false;
        if (!isValidNameField(firstName, true)) return false;
        if (!isValidNameField(primerApellido, true)) return false;
        if (!isValidNameField(data.segundoApellido, false)) return false;
        if (data.fatherUnknown !== "yes" && !data.fatherFirstName.trim()) return false;
        if (data.motherUnknown !== "yes" && !data.motherFirstName.trim()) return false;
        if (!data.civilStatus) return false;
        if (email.trim() && !isValidEmail(email)) return false;
        if (!isValidRequiredPhoneParts(data.phoneCountryCode, phone)) return false;
        if (data.registrantType === "representative") {
          if (
            !repRelationship.trim() ||
            !applicantFirstName.trim() ||
            !applicantPrimerApellido.trim()
          ) {
            return false;
          }
          if (!isValidNameField(applicantFirstName, true)) return false;
          if (!isValidNameField(applicantPrimerApellido, true)) return false;
          if (!isValidNameField(data.applicantSegundoApellido, false)) return false;
          const appEmail = data.applicantEmail.trim();
          if (appEmail && !isValidEmail(appEmail)) return false;
          if (
            data.applicantPhone.trim() &&
            !isValidOptionalPhoneParts(data.applicantPhoneCountryCode, data.applicantPhone)
          ) {
            return false;
          }
          if (contactAssignedTo === "applicant" && !repPhoneOtpVerified) return false;
        }
        return true;
      }
      case 2: {
        if (eligibilitySubstep === "asylum") return data.asylumRoutingConfirmed !== null;
        return !!data.qualifyingGround;
      }
      case 3: {
        return getAddressStep5FirstInvalidFieldId(data) === null;
      }
      case 4: {
        return (
          data.engagementConsent === true &&
          data.dataProcessingConsent === true &&
          isEngagementSignatureDataUrlValid(data.signature)
        );
      }
      default:
        return true;
    }
  })();

  const getFirstInvalidFieldId = (): string | null => {
    switch (currentStep) {
      case 1:
        if (!(data.registrantType === "individual" || data.registrantType === "representative")) return "field-registrantType";
        if (!data.firstName.trim() || !isValidNameField(data.firstName, true)) return "field-firstName";
        if (!data.primerApellido.trim() || !isValidNameField(data.primerApellido, true)) return "field-primerApellido";
        if (!isValidNameField(data.segundoApellido, false)) return "field-segundoApellido";
        if (data.fatherUnknown !== "yes" && !data.fatherFirstName.trim()) return "field-fatherFirstName";
        if (data.motherUnknown !== "yes" && !data.motherFirstName.trim()) return "field-motherFirstName";
        if (!data.civilStatus) return "field-civilStatus";
        if (data.email.trim() && !isValidEmail(data.email)) return "field-email";
        if (!data.phone.trim() || !isValidRequiredPhoneParts(data.phoneCountryCode, data.phone)) {
          return "field-phone";
        }
        if (data.registrantType === "representative") {
          if (!data.repRelationship.trim()) return "field-repRelationship";
          if (!data.applicantFirstName.trim() || !isValidNameField(data.applicantFirstName, true)) return "field-applicantFirstName";
          if (!data.applicantPrimerApellido.trim() || !isValidNameField(data.applicantPrimerApellido, true)) return "field-applicantPrimerApellido";
          if (!isValidNameField(data.applicantSegundoApellido, false)) return "field-applicantSegundoApellido";
          const appEmail = data.applicantEmail.trim();
          if (appEmail && !isValidEmail(appEmail)) return "field-applicantEmail";
          if (
            data.applicantPhone.trim() &&
            !isValidOptionalPhoneParts(data.applicantPhoneCountryCode, data.applicantPhone)
          ) {
            return "field-applicantPhone";
          }
          if (data.contactAssignedTo === "applicant" && !data.repPhoneOtpVerified) return "field-repPhoneOtpVerified";
        }
        return null;
      case 2: {
        if (eligibilitySubstep === "asylum") {
          if (data.asylumRoutingConfirmed === null) return "field-asylumRoutingConfirmed";
          return null;
        }
        if (!data.qualifyingGround) return "field-qualifyingGround";
        return null;
      }
      case 3: {
        return getAddressStep5FirstInvalidFieldId(data);
      }
      case 4:
        if (!data.engagementConsent) return "field-engagementConsent";
        if (!data.dataProcessingConsent) return "field-dataProcessingConsent";
        if (!data.signature || !isEngagementSignatureDataUrlValid(data.signature)) return "field-signature";
        return null;
      default:
        return null;
    }
  };

  const scrollAndHighlightInvalidField = () => {
    const root = mainRef.current || document;
    const byId = (id: string) =>
      root.querySelector<HTMLElement>(`#${id}`) || document.getElementById(id);
    const firstInvalidFieldId = getFirstInvalidFieldId();
    if (!firstInvalidFieldId) return;
    const target = byId(firstInvalidFieldId);
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.remove("app-field-attention");
    void target.offsetWidth;
    target.classList.add("app-field-attention");
    setInvalidFieldId(firstInvalidFieldId);
  };

  return (
    <div
      className="w-full overflow-hidden bg-[#F8F7F4] flex flex-col"
      style={{ height: "100dvh" }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <Header onHelp={handleHelp} onLogoClick={goToOrganizer} />

      <main ref={mainRef} className="flex-1 overflow-y-auto overflow-x-hidden w-full">
        <ProgressBar />
        {previousHub === "organizer" && (
          <button
            type="button"
            onClick={() => {
              goToOrganizer();
              setPreviousHub(null);
            }}
            className="flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-700 cursor-pointer transition-colors mb-3 px-4 pt-3"
          >
            {t("nav.backToOrganizer")}
          </button>
        )}
        <div key={currentStep} className="section-enter w-full">
          {renderSection()}
        </div>
        {stepError && (
          <div className="max-w-[480px] mx-auto px-4">
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{stepError}</p>
          </div>
        )}
        <FormNavigation
          isLastStep={currentStep === totalSteps}
          canContinue={canContinue}
          onContinue={async () => {
            if (!canContinue) {
              scrollAndHighlightInvalidField();
              return false;
            }
            const ok = await handleStepContinue();
            if (!ok) return false;
            if (currentStep === 2 && eligibilitySubstep === "asylum" && data.asylumRoutingConfirmed === false) {
              setEligibilitySubstep("qualifying");
              return false;
            }
            if (currentStep === 3) {
              openDocuments("identity");
              return false;
            }
            if (currentStep === 4) {
              setPreviousHub(null);
              goToOrganizer();
              return false;
            }
            return true;
          }}
          onBack={() => {
            if (currentStep === 2 && eligibilitySubstep === "qualifying") {
              setEligibilitySubstep("asylum");
              return;
            }
            if (currentStep === 4 && previousHub === "organizer") {
              goToOrganizer();
              return;
            }
            goBack();
          }}
          continueLoading={stepSaving}
        />
        <Footer />
      </main>

      {/* Help system kept in place, but the floating round ? trigger is suppressed for now. */}

      {helpOpen && <HelpOverlay onClose={handleCloseHelp} fieldTag={helpField} />}

      {showExitModal && (
        <ExitModal onStay={handleExitStay} onLeave={handleExitLeave} />
      )}
      <style>{`
        .app-field-attention {
          box-shadow: 0 0 0 3px #fff, 0 0 0 5px #f43f5e !important;
          border-radius: 12px;
          outline: none !important;
        }
      `}</style>
    </div>
  );
}
